// src/pages/gt3x/RegisterList.jsx
import { useEffect, useMemo, useState } from "react";
import PageHero from "../../components/PageHero.jsx";
import { fetchSheetViaProxy } from "../../lib/sheets";

const RANGE = "Anmeldungen!A1:K200";

export default function RegisterList() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                setLoading(true);
                setError("");

                const { rows: raw } = await fetchSheetViaProxy({ range: RANGE });

                const sample = raw[0] || {};
                const keys = Object.keys(sample);

                const tsKey =
                    keys.find((k) => k.toLowerCase().includes("zeitstempel")) ||
                    keys.find((k) => k.toLowerCase().includes("timestamp")) ||
                    "Zeitstempel";

                const cleaned = raw.map((r, idx) => {
                    const fullName = (r["Vor- und Nachname"] || "").trim();
                    const teamRaw = (r["Teamname"] || "").trim();

                    const upperValues = Object.values(r || {}).map((v) =>
                        String(v ?? "").toUpperCase()
                    );

                    const isWaitlistSep =
                        !fullName && upperValues.some((val) => val.includes("WARTELISTE"));

                    return {
                        id: r["ID"] || String(idx + 1),
                        ts: r[tsKey] || "",
                        fullName,
                        iracingName: r["iRacing Name"] || "",
                        iracingId: r["iRacing ID"] || "",
                        team: teamRaw,
                        numPro: r["Startnummer PRO"] || "",
                        numInt: r["Startnummer INT"] || "",
                        numAm: r["Startnummer AM"] || "",
                        klasse: r["Klasse"] || "",
                        fee: r["Startgeld"] || "",
                        isWaitlistSep,
                    };
                });

                const onlyWithName = cleaned.filter(
                    (r) =>
                        r.isWaitlistSep ||
                        (r.fullName && r.fullName.trim() !== "")
                );

                if (!cancelled) {
                    setRows(onlyWithName);
                }
            } catch (err) {
                console.error(err);

                if (!cancelled) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Unbekannter Fehler beim Laden."
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const normalizeNr = (v) => String(v ?? "").trim();

    const dupMaps = useMemo(() => {
        const countMap = (key) => {
            const m = new Map();

            for (const r of rows) {
                if (r.isWaitlistSep) continue;

                const val = normalizeNr(r[key]);
                if (!val) continue;

                m.set(val, (m.get(val) || 0) + 1);
            }

            return m;
        };

        const pro = countMap("numPro");
        const int = countMap("numInt");
        const am = countMap("numAm");

        const isDup = (map, value) => {
            const v = normalizeNr(value);
            return v && (map.get(v) || 0) > 1;
        };

        return { pro, int, am, isDup };
    }, [rows]);

    const stats = useMemo(() => {
        const driverRows = rows.filter((r) => !r.isWaitlistSep);
        const paid = driverRows.filter((r) => String(r.fee).toUpperCase() === "TRUE").length;

        const pro = driverRows.filter((r) => r.klasse === "PRO").length;
        const int = driverRows.filter((r) => r.klasse === "INT").length;
        const am = driverRows.filter((r) => r.klasse === "AM").length;

        return {
            total: driverRows.length,
            paid,
            pro,
            int,
            am,
        };
    }, [rows]);

    if (loading) {
        return (
            <div className="rv-page">
                <PageHero
                    kicker="GT3 Xperience"
                    title="Anmeldeliste"
                >
                    Die aktuelle Anmeldeliste wird aus dem Google Sheet geladen.
                </PageHero>

                <section className="rv-modern-card">
                    <p className="rv-modern-muted">Die Anmeldungen werden geladen…</p>
                </section>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rv-page">
                <PageHero
                    kicker="GT3 Xperience"
                    title="Anmeldeliste"
                >
                    Die aktuelle Anmeldeliste konnte nicht geladen werden.
                </PageHero>

                <section className="rv-modern-card">
                    <div className="rv-section-head">
                        <div>
                            <div className="rv-section-kicker">Fehler</div>
                            <h2>Fehler beim Laden der Anmeldungen</h2>
                        </div>

                        <span className="rv-status-pill rv-status-pill--warn">
                            Shieet Fehler
                        </span>
                    </div>

                    <p style={{ color: "var(--err)" }}>{error}</p>
                </section>
            </div>
        );
    }

    return (
        <div className="rv-page">
            <PageHero
                kicker="GT3 Xperience"
                title="Anmeldeliste"
            >
                Hier siehst du die aktuell angemeldeten Fahrer der GT3 Xperience.
                Der Fahrername wird grün eingefärbt, sobald die Startgebühr entrichtet wurde.
            </PageHero>

            <section className="rv-modern-card rv-modern-card--accent">
                <div className="rv-section-head">
                    <div>
                        <div className="rv-section-kicker">Entry List</div>
                        <h2>Aktuelle Anmeldungen</h2>
                    </div>

                    <div className="rv-pill-row">
                        <span className="rv-status-pill rv-status-pill--info">
                            Gesamt: {stats.total}
                        </span>
                        <span className="rv-status-pill rv-status-pill--ok">
                            Bezahlt: {stats.paid}
                        </span>
                    </div>
                </div>

                <div className="rv-info-grid" style={{ marginBottom: 16 }}>
                    <div className="rv-info-tile">
                        <div className="rv-info-tile__label">
                            <span className="rv-class-word rv-class-word--pro">PRO</span>
                        </div>
                        <div className="rv-info-tile__value">{stats.pro}</div>
                    </div>

                    <div className="rv-info-tile">
                        <div className="rv-info-tile__label">
                            <span className="rv-class-word rv-class-word--int">INT</span>
                        </div>
                        <div className="rv-info-tile__value">{stats.int}</div>
                    </div>

                    <div className="rv-info-tile">
                        <div className="rv-info-tile__label">
                            <span className="rv-class-word rv-class-word--am">AM</span>
                        </div>
                        <div className="rv-info-tile__value">{stats.am}</div>
                    </div>

                    <div className="rv-info-tile">
                        <div className="rv-info-tile__label">Datenquelle</div>
                        <div className="rv-info-tile__value">Google Sheet</div>
                    </div>
                </div>

                <div className="fancyTable regTable-wrapper rv-reglist-table-wrap">
                    <table className="tbl regTable rv-reglist-table">
                        <thead>
                        <tr>
                            <th>#</th>
                            <th className="align-left">Fahrer</th>
                            <th className="align-left">iRacing</th>
                            <th className="align-left">Team</th>
                            <th>Startnummern</th>
                            <th>Klasse</th>
                            <th>Startgeld</th>
                        </tr>
                        </thead>

                        <tbody>
                        {(() => {
                            let driverNo = 0;

                            return rows.map((r) => {
                                if (r.isWaitlistSep) {
                                    return (
                                        <tr
                                            key={`waitlist-sep-${r.id || r.ts || "x"}`}
                                            className="reg-waitlist-sep"
                                        >
                                            <td colSpan={7}>
                                                {r.team || "WARTELISTE"}
                                            </td>
                                        </tr>
                                    );
                                }

                                driverNo += 1;

                                const hasPaid = String(r.fee).toUpperCase() === "TRUE";

                                const classKey = String(r.klasse || "").trim().toLowerCase();

                                const classChipClass =
                                    classKey === "pro"
                                        ? "rv-class-chip rv-class-chip--pro"
                                        : classKey === "int"
                                            ? "rv-class-chip rv-class-chip--int"
                                            : classKey === "am"
                                                ? "rv-class-chip rv-class-chip--am"
                                                : "rv-class-chip";

                                const proDup = dupMaps.isDup(dupMaps.pro, r.numPro);
                                const intDup = dupMaps.isDup(dupMaps.int, r.numInt);
                                const amDup = dupMaps.isDup(dupMaps.am, r.numAm);

                                return (
                                    <tr key={`${r.id}-${r.iracingId}-${r.fullName}`}>
                                        <td>{driverNo}</td>

                                        <td className={`align-left ${hasPaid ? "rv-reglist-paid-driver" : ""}`}>
                                            <div className="rv-reg-driver-main">
                                                {r.fullName}
                                            </div>
                                        </td>

                                        <td className="align-left">
                                            <div className="rv-reg-driver-main">
                                                {r.iracingName || "—"}
                                            </div>

                                            {r.iracingId && (
                                                <div className="rv-reg-driver-sub">
                                                    ID {r.iracingId}
                                                </div>
                                            )}
                                        </td>

                                        <td className="align-left">
                                            {r.team || "—"}
                                        </td>

                                        <td>
                                            <div className="rv-startnr-row">
                                                {r.numPro && (
                                                    <span
                                                        className={
                                                            "rv-startnr-chip rv-startnr-chip--pro" +
                                                            (proDup ? " rv-startnr-chip--dup" : "")
                                                        }
                                                        title={proDup ? "Doppelte PRO-Startnummer" : "PRO"}
                                                    >
                                PRO {r.numPro}
                            </span>
                                                )}

                                                {r.numInt && (
                                                    <span
                                                        className={
                                                            "rv-startnr-chip rv-startnr-chip--int" +
                                                            (intDup ? " rv-startnr-chip--dup" : "")
                                                        }
                                                        title={intDup ? "Doppelte INT-Startnummer" : "INT"}
                                                    >
                                INT {r.numInt}
                            </span>
                                                )}

                                                {r.numAm && (
                                                    <span
                                                        className={
                                                            "rv-startnr-chip rv-startnr-chip--am" +
                                                            (amDup ? " rv-startnr-chip--dup" : "")
                                                        }
                                                        title={amDup ? "Doppelte AM-Startnummer" : "AM"}
                                                    >
                                AM {r.numAm}
                            </span>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            {r.klasse ? (
                                                <span className={classChipClass}>
                            {r.klasse}
                        </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>

                                        <td>
                                            {hasPaid ? (
                                                <span className="rv-paid-dot">✅</span>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                    </tr>
                                );
                            });
                        })()}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}